/**
 * 長崎県の背景ツアー「長崎めぐり」コンテンツ。
 *
 * 軸は時間帯。壱岐・対馬の夜明けから始めて、長崎市の夜で終わる。
 * 市町の内訳は壱岐・対馬・平戸・佐世保・雲仙・島原・新上五島が各1、長崎市が3。
 *
 * 静かに扱う景色（solemn）は3つ。平和公園、端島（軍艦島）、頭ヶ島天主堂。
 * 広島の原爆ドームと同じ方針で、公式資料の位置づけから外れない範囲で事実だけを書く。
 * 観光消費的な語（絶景・映え・廃墟）、感嘆符、驚き型の見出しを使わない。
 * 犠牲者の数を見出しに置かない。クイズにしない。
 *
 * 調査で出てきた「書いてはいけないこと」を反映している。
 *  - 「世界新三大夜景」「日本新三大夜景」は民間団体の選定。稲佐山では使わない。
 *  - 大瀬崎断崖の延長は環境省の中で15kmと20kmが両方ある。数字を書かない。
 *  - 九十九島の島数は環境省が「208（江迎湾〜俵ヶ浦）」と「200以上」の両方。範囲つきで書く。
 *  - 雲仙は「我が国最初の国立公園の一つ」。「最初の国立公園」と書かない。
 *  - 世界遺産の名称は「潜伏キリシタン」。「隠れキリシタン」と書かない（歴史学上も別概念）。
 *  - 端島の世界遺産の構成資産名は「端島炭坑」。「軍艦島」は通称。
 *  - 猿岩の「神々の八本柱」は土地の言い伝え。事実として書かない。
 *  - 稲佐山のスロープカーは2026年10月2日まで運休だが、期限つきの情報なので豆知識にしない。
 */

import type { StageTour } from '../types';

export const nagasakiSceneryTour: StageTour = {
  scenes: [
    {
      id: 'saruiwa',
      name: '猿岩',
      area: '壱岐市・夜明け',
      image: '/assets/scenery/nagasaki/scenes/01_saruiwa.webp',
      hotspots: [
        {
          id: 'saruiwa-45m',
          x: 0.44,
          y: 0.3,
          title: '高さ45メートルの岩',
          body: '壱岐のシンボルで、黒崎半島の先端に立つ高さ45メートルの岩です。自然によって造られた奇岩で、「そっぽを向いたサル」にそっくりな姿をしています。',
          source: 'https://www.ikikankou.com/spot/10093',
          mapQuery: '猿岩 壱岐',
        },
        {
          id: 'saruiwa-hashira',
          x: 0.66,
          y: 0.44,
          title: '島をつなぎ止めた柱',
          body: '壱岐には、島が流されないよう神々が八本の柱を立てたという言い伝えがあり、猿岩はその一本とされています。土地に伝わる話で、事実として確かめられたものではありません。',
          source: 'https://www.ikikankou.com/spot/10093',
          mapQuery: '猿岩 壱岐',
        },
        {
          id: 'saruiwa-hodai',
          x: 0.22,
          y: 0.5,
          title: '撃たれなかった砲台',
          body: '猿岩のそばに黒崎砲台跡があります。昭和8年に完成し、口径41センチの砲を2門備えていましたが、一度も実戦で使われないまま終戦後に解体されました。いまは地上から大穴を見下ろす形の見学になります。',
          source: 'https://www.ikikankou.com/spot/10104',
          mapQuery: '黒崎砲台跡',
        },
      ],
    },
    {
      id: 'eboshidake',
      name: '烏帽子岳展望所と浅茅湾',
      area: '対馬市・朝',
      image: '/assets/scenery/nagasaki/scenes/02_eboshidake.webp',
      hotspots: [
        {
          id: 'eboshi-360',
          x: 0.5,
          y: 0.2,
          title: '三百六十度が開けている',
          body: '烏帽子岳は標高176メートル。対馬の中央に広がる浅茅湾の北岸にあり、展望所からぐるりと見渡せます。山頂近くまで道路と駐車場があり、そこから階段を10分ほど上ります。',
          source: 'https://www.tsushima-net.org/tourism-history/eboshidake',
          mapQuery: '烏帽子岳展望所 対馬',
        },
        {
          id: 'asou-wan',
          x: 0.34,
          y: 0.42,
          title: '沈んだ谷が湾になった',
          body: '浅茅湾は面積53.61平方キロメートル、湾内のいちばん深いところで80メートルあります。陸の谷が海に沈んでできた複雑な地形で、環境省の資料では「溺れ谷地形」と説明されています。',
          source: 'https://www.env.go.jp/water/heisa/heisa_net/waters/asowan.html',
          mapQuery: '浅茅湾',
        },
        {
          id: 'asou-yoshoku',
          x: 0.7,
          y: 0.48,
          title: '湾のなかで育てる',
          body: '浅茅湾では真珠やブリの養殖が行われています。湾口の幅は4.18キロメートルほどで、内側に入り組んだ溺れ谷地形が広がります。',
          source: 'https://www.env.go.jp/water/heisa/heisa_net/waters/asowan.html',
          mapQuery: '浅茅湾',
        },
      ],
    },
    {
      id: 'shiodawara',
      name: '塩俵の断崖',
      area: '平戸市 生月島・午前',
      image: '/assets/scenery/nagasaki/scenes/03_shiodawara.webp',
      hotspots: [
        {
          id: 'shiodawara-chujo',
          x: 0.4,
          y: 0.34,
          title: '柱が並ぶ壁',
          body: '生月島の西海岸に、玄武岩の溶岩が冷えるときにできた柱状節理が並びます。柱の太さは60から120センチ、断面は五角形から六角形。南北におよそ500メートル続き、厚みは20メートルを超えます。',
          source: 'https://www.pref.nagasaki.jp/bunkadb/index.php/view/400',
          mapQuery: '塩俵の断崖',
        },
        {
          id: 'shiodawara-shitei',
          x: 0.66,
          y: 0.26,
          title: '県の天然記念物',
          body: '「生月町塩俵断崖の柱状節理」として、1989年9月29日に長崎県の天然記念物に指定されています。所在地は平戸市生月町壱部免です。',
          source: 'https://www.pref.nagasaki.jp/bunkadb/index.php/view/400',
          mapQuery: '塩俵の断崖',
        },
        {
          id: 'nakaenoshima',
          x: 0.2,
          y: 0.22,
          title: '沖に浮かぶ小さな島',
          body: '平戸島の北西岸の沖合2キロに中江ノ島があります。東西およそ400メートル、標高34.6メートルの無人島で、禁教の時代に処刑が行われた記録が残ります。世界遺産「長崎と天草地方の潜伏キリシタン関連遺産」の構成資産です。',
          source: 'https://kirishitan.jp/components/com003',
          mapQuery: '中江ノ島 平戸',
        },
      ],
    },
    {
      id: 'kujukushima',
      name: '九十九島',
      area: '佐世保市・正午',
      image: '/assets/scenery/nagasaki/scenes/04_kujukushima.webp',
      hotspots: [
        {
          id: 'kujuku-208',
          x: 0.5,
          y: 0.36,
          title: '数えた島は208',
          body: '環境省の西海国立公園の資料では、江迎湾から俵ヶ浦までの範囲で島数208と記載されています。小さな島が密に集まった地形で、公園全体では大小400あまりの島々からなります。',
          source: 'https://www.env.go.jp/park/saikai/point/index.html',
          mapQuery: '九十九島',
        },
        {
          id: 'kujuku-mitsudo',
          x: 0.26,
          y: 0.44,
          title: '島の密度が全国一',
          body: '環境省は九十九島を「全国一の島密度を誇る」と紹介しています。島の地形と地質の違いから北と南に分かれ、展望所ごとに違った見え方をします。',
          source: 'https://www.env.go.jp/park/saikai/guide/view.html',
          mapQuery: '九十九島',
        },
        {
          id: 'saikai-1955',
          x: 0.74,
          y: 0.28,
          title: '昭和30年からの国立公園',
          body: '西海国立公園は1955年3月16日に指定されました。九十九島のほか、平戸や五島の島々も含みます。',
          source: 'https://www.env.go.jp/park/saikai/point/index.html',
          mapQuery: '西海国立公園',
        },
      ],
    },
    {
      id: 'hashima',
      name: '端島（軍艦島）',
      area: '長崎市・昼過ぎ',
      image: '/assets/scenery/nagasaki/scenes/05_hashima.webp',
      solemn: true,
      hotspots: [
        {
          id: 'hashima-tanko',
          x: 0.5,
          y: 0.34,
          title: '海の底に炭鉱があった',
          body: '端島は岩礁を埋め立てた人工島で、海底の炭鉱がありました。1960年代には島のなかにおよそ5300人が暮らし、学校や病院もありました。石炭から石油へエネルギーが移り、1974年に閉山しています。',
          source: 'https://www.at-nagasaki.jp/spot/51797',
          mapQuery: '端島 軍艦島',
        },
        {
          id: 'hashima-joriku',
          x: 0.26,
          y: 0.46,
          title: '上陸できる日は限られる',
          body: '上陸は長崎市の基準を満たした日にだけ行われます。波の高さ、風向き、船の位置での風速、周辺の見通しが決められていて、最後は船長が安全に乗り降りできると判断したときに限られます。',
          source: 'https://www.city.nagasaki.lg.jp/teian/31312.html',
          mapQuery: '端島 軍艦島',
        },
        {
          id: 'hashima-hozon',
          x: 0.72,
          y: 0.42,
          title: '建物は傷んでいる',
          body: '閉山から50年以上が経ち、建物の傷みが進んでいます。長崎市は比較的傷みの少ない3号棟から保存を始める方針で、新しい見学通路の整備や状態の観測を検討しています。',
          source: 'https://www.city.nagasaki.lg.jp/kanko/840000/843000/p029703.html',
          mapQuery: '端島 軍艦島',
        },
      ],
    },
    {
      id: 'unzen-jigoku',
      name: '雲仙地獄',
      area: '雲仙市・午後',
      image: '/assets/scenery/nagasaki/scenes/06_unzen_jigoku.webp',
      hotspots: [
        {
          id: 'unzen-1934',
          x: 0.5,
          y: 0.22,
          title: '最初の国立公園のひとつ',
          body: '雲仙は1934年3月16日、日本で最初の国立公園のひとつとして指定されました。1956年に天草地域が加わり、いまの雲仙天草国立公園になっています。',
          source: 'https://www.env.go.jp/park/unzen/point/index.html',
          mapQuery: '雲仙地獄',
        },
        {
          id: 'unzen-30',
          x: 0.3,
          y: 0.44,
          title: '三十あまりの地獄',
          body: '大叫喚、お糸、清七など、三十あまりの地獄があります。まわりには木製の遊歩道が整えられています。噴気には硫化水素が含まれ、強い硫黄のにおいがします。',
          source: 'https://unzenonsen.unzen.org/watch/jigoku/',
          mapQuery: '雲仙地獄',
        },
        {
          id: 'unzen-junkyo',
          x: 0.72,
          y: 0.38,
          title: '殉教の地でもある',
          body: '江戸時代のキリシタン弾圧では、雲仙地獄も信者を改宗させる手段として使われ、拷問が行われました。毎年5月の第3日曜日に、この地でキリシタン殉教祭が行われています。',
          source: 'https://unzenonsen.unzen.org/watch/jigoku/',
          mapQuery: '雲仙地獄',
        },
      ],
    },
    {
      id: 'koi-no-oyogu-machi',
      name: '鯉の泳ぐまち',
      area: '島原市・午後遅く',
      image: '/assets/scenery/nagasaki/scenes/07_koi_no_oyogu_machi.webp',
      hotspots: [
        {
          id: 'koi-chonaikai',
          x: 0.46,
          y: 0.46,
          title: '町内会が放した鯉',
          body: '島原市新町一帯の、全長100メートルの水路です。1978年7月に町内会が色とりどりの鯉を放流したのが始まりで、いまは町の名前になっています。',
          source: 'https://www.city.shimabara.lg.jp/page942.html',
          mapQuery: '鯉の泳ぐまち 島原',
        },
        {
          id: 'shimabara-meisui',
          x: 0.24,
          y: 0.32,
          title: '名水百選の湧き水',
          body: '島原の湧水は、1985年1月に環境庁（当時）から「島原湧水群」として日本名水百選に選ばれています。水路を流れているのもこの湧き水です。',
          source: 'https://www.city.shimabara.lg.jp/page942.html',
          mapQuery: '島原湧水群',
        },
        {
          id: 'shimabara-kanzarashi',
          x: 0.72,
          y: 0.36,
          title: '湧き水で冷やす甘味',
          body: '島原のかんざらしは、この湧水で白玉を冷やし、砂糖の蜜をかけたものです。砂糖が貴重だったころは夏のもてなしの菓子でした。',
          source:
            'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/46_30_nagasaki.html',
          relatedItemId: 'kanzarashi',
          mapQuery: '島原 かんざらし',
        },
      ],
    },
    {
      id: 'kashiragashima',
      name: '頭ヶ島天主堂',
      area: '新上五島町・夕方',
      image: '/assets/scenery/nagasaki/scenes/08_kashiragashima.webp',
      solemn: true,
      hotspots: [
        {
          id: 'kashira-ishi',
          x: 0.5,
          y: 0.38,
          title: '石を積んで建てた',
          body: '地元で採れる砂岩を水平に積み上げた、粗い表面の石壁の教会堂です。長崎や地元の石工に地元の信徒も加わって建てられ、1910年の着工から1919年の完成まで、およそ10年かかりました。',
          source: 'https://shinkamigoto.nagasaki-tabinet.com/junrei/10239',
          mapQuery: '頭ヶ島天主堂',
        },
        {
          id: 'kashira-renraku',
          x: 0.26,
          y: 0.5,
          title: '見学は事前連絡を',
          body: '世界遺産に関わる教会では、見学の事前連絡への協力が呼びかけられています。教会は「祈りの場」であり、教会の行事などで見学できない日もあります。',
          source: 'https://kyoukaigun.jp/',
          mapQuery: '頭ヶ島天主堂',
        },
        {
          id: 'kashira-ryoyo',
          x: 0.72,
          y: 0.46,
          title: '療養の島に移り住んだ',
          body: '頭ヶ島は病人の療養地として使われていた島でした。外海地域の潜伏キリシタンがここへ移り住み、共同体を保ちました。世界遺産の構成資産「頭ヶ島の集落」です。',
          source: 'https://kirishitan.jp/components/com009',
          mapQuery: '頭ヶ島の集落',
        },
      ],
    },
    {
      id: 'heiwa-koen',
      name: '平和公園',
      area: '長崎市・日没前',
      image: '/assets/scenery/nagasaki/scenes/09_heiwa_koen.webp',
      solemn: true,
      hotspots: [
        {
          id: 'heiwa-1102',
          x: 0.5,
          y: 0.44,
          title: '1945年8月9日11時2分',
          body: '1945年8月9日午前11時2分、この地の上空500メートルで原子爆弾がさく裂し、多くの市民の命が奪われました。毎年8月9日に平和祈念式典が行われています。',
          source: 'https://www.city.nagasaki.lg.jp/page/44706.html',
          mapQuery: '長崎 平和公園',
        },
        {
          id: 'heiwa-kinenzo',
          x: 0.3,
          y: 0.3,
          title: '平和祈念像',
          body: '高さ9.7メートル、重さおよそ30トンの銅像です。彫刻家・北村西望の作で、1955年に完成しました。右手は原爆を、左手は平和を示し、顔は戦争の犠牲者の冥福を祈る表情とされています。',
          source: 'https://www.city.nagasaki.lg.jp/page/44706.html',
          mapQuery: '長崎 平和祈念像',
        },
        {
          id: 'heiwa-chushinchi',
          x: 0.72,
          y: 0.36,
          title: '原爆落下中心地碑',
          body: '原爆の落下中心地を示す、黒御影石でできた高さおよそ6.5メートルの塔です。近くには被爆当時の地層が、当時の実相を示す資料として現地に保存されています。',
          source: 'https://www.city.nagasaki.lg.jp/page/44706.html',
          mapQuery: '長崎 原爆落下中心地',
        },
      ],
    },
    {
      id: 'inasayama',
      name: '稲佐山',
      area: '長崎市・夜',
      image: '/assets/scenery/nagasaki/scenes/10_inasayama.webp',
      hotspots: [
        {
          id: 'inasa-333',
          x: 0.44,
          y: 0.34,
          title: '標高333メートル',
          body: '稲佐山の山頂は標高333メートル。展望台からは「鶴の港」と呼ばれる長崎港を見下ろし、360度の眺めが開けます。',
          source: 'https://www.city.nagasaki.lg.jp/page/79583.html',
          mapQuery: '稲佐山展望台',
        },
        {
          id: 'inasa-goto',
          x: 0.68,
          y: 0.44,
          title: '昼は五島まで見える',
          body: '晴れた日の昼間は、長崎の市街地だけでなく、雲仙・天草・五島列島まで見渡せます。',
          source: 'https://www.nagasaki-tabinet.com/guide/115',
          mapQuery: '稲佐山展望台',
        },
      ],
    },
  ],
};
